"use client";

import React from "react";

export interface InfoPoint {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

export interface InfoSectionWithVisualProps {
  title: string;
  paragraphs: string[];
  visual?: {
    type: "points" | "custom";
    points?: InfoPoint[];
    customContent?: React.ReactNode;
  };
  visualPosition?: "left" | "right";
}

export function InfoSectionWithVisual({
  title,
  paragraphs,
  visual,
  visualPosition = "right"
}: InfoSectionWithVisualProps) {
  const textContent = (
    <div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
        {title}
      </h2>
      <div className="prose prose-lg text-gray-700 space-y-6">
        {paragraphs.map((paragraph, idx) => (
          <p key={idx}>{paragraph}</p>
        ))}
      </div>
    </div>
  );

  const visualContent = visual && (
    <div className="relative">
      {visual.type === "points" && visual.points && (
        <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            {visual.points.map((point, idx) => (
              <div key={idx} className={`flex items-center gap-4 ${idx < visual.points!.length - 1 ? 'mb-4' : ''}`}>
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  {point.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{point.title}</h3>
                  <p className="text-sm text-gray-600">{point.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {visual.type === "custom" && visual.customContent}
    </div>
  );

  return (
    <section className="py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {visualPosition === "left" ? (
            <>
              {visualContent}
              {textContent}
            </>
          ) : (
            <>
              {textContent}
              {visualContent}
            </>
          )}
        </div>
      </div>
    </section>
  );
}